import type { ProductListType } from "src/types/product-listing";
import { PRODUCT_LIST_TYPE } from "./constants";
import type { PopularCategoryType } from "src/types/Category";
import type {
  CatalogProduct,
  HomepagePopularCategoryData,
  HomePageSingleBrand,
  HomepageSingleCategoryProduct,
} from "src/types/graphql.d";
import type {
  ListingFacetBrandData,
  ListingFacetCategoryData,
  ListingFacetColorData,
  ListingFacetData,
  ListingSortKeys,
} from "src/types/adapters/listing.type";
import type { SignInOrSignUpRequestBody } from "src/types/sign-in-or-sign-up";
import type { ValidationContext } from "src/types/validation-schema";

export function isSignInOrSignUpRequestBody(
  obj: unknown
): obj is SignInOrSignUpRequestBody {
  return (
    typeof obj === "object" &&
    obj !== null &&
    ("skipSignIn" in obj || "callbackUrl" in obj)
  );
}

type SignUpAtCheckoutSchema = ValidationContext & {
  shouldCreateAccount: boolean;
  password: string;
};

export function isSignUpAtCheckoutSchema(
  obj: unknown
): obj is SignUpAtCheckoutSchema {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "shouldCreateAccount" in obj &&
    "password" in obj
  );
}

export function isProductListType(value: string): value is ProductListType {
  return Object.values<string>(PRODUCT_LIST_TYPE).includes(value);
}

export function isObjectSchema(obj: unknown): obj is { __typename: string } {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  return typeof obj === "object" && "__typename" in obj;
}

export function isHomepagePopularCategoryData(
  obj: unknown
): obj is HomepagePopularCategoryData {
  return (
    isObjectSchema(obj) && obj.__typename === "HomepagePopularCategoryData"
  );
}

export function isPopularCategoryType(
  obj: unknown
): obj is PopularCategoryType {
  return isObjectSchema(obj) && obj.__typename === "PopularCategory";
}

export function isCategoryData(
  obj: unknown
): obj is HomepagePopularCategoryData {
  return isObjectSchema(obj) && obj.__typename === "CategoryData";
}

export function isHomepageSingleCategoryProduct(
  obj: unknown
): obj is HomepageSingleCategoryProduct {
  return (
    isObjectSchema(obj) && obj.__typename === "HomepageSingleCategoryProduct"
  );
}

export function isCatalogProduct(obj: unknown): obj is CatalogProduct {
  return isObjectSchema(obj) && obj.__typename === "CatalogProduct";
}

export function isHomePageSingleBrand(
  obj: unknown
): obj is HomePageSingleBrand {
  return isObjectSchema(obj) && obj.__typename === "HomePageSingleBrand";
}

export function isExpectedData<T>(
  obj: unknown,
  validKeys: readonly string[]
): obj is T {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  return Object.keys(obj).some((value) => validKeys.includes(value));
}

export function isListingFacetColorData(
  option: ListingFacetData
): option is ListingFacetColorData {
  return "hex" in option && "icon" in option;
}

export function isListingFacetCategoryData(
  option: ListingFacetData
): option is ListingFacetCategoryData {
  return (
    "categoryPageId" in option &&
    "parentPathString" in option &&
    "id" in option &&
    "parent" in option &&
    "slug" in option
  );
}

export function isListingFacetBrandData(
  option: ListingFacetData
): option is ListingFacetBrandData {
  return (
    "designerBrand" in option &&
    "logo" in option &&
    "id" in option &&
    "slug" in option
  );
}

export function isListingSortKey(value: unknown): value is ListingSortKeys {
  return (
    value === "popularity" ||
    value === "new" ||
    value === "new-discount" ||
    value === "price-high-to-low" ||
    value === "price-low-to-high"
  );
}
